/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Button, Divider, Link
} from 'components/ui';
import Grid from 'components/Grid/Grid';
import UseAGiftCard from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/UseAGiftCard';
import AppliedGiftCards from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/AppliedGiftCards';
import Apple from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Apple/Apple';
import Paypal from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paypal/Paypal';
import Klarna from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Klarna/Klarna';
import Venmo from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo/Venmo';
import { AfterPayPill } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AfterPay';
import Paze from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paze/Paze';
import StringUtils from 'utils/String';
import pazeUtils from 'utils/Paze';
import checkoutUtils from 'utils/Checkout';

/**
 * NewUserPaymentView - Payment section for new users with no saved payment method
 * Shows "Add New Card" button, "Use a Gift Card" link, and "Other Ways to Pay" section as pill buttons
 * @param {Object} props - Component props
 * @param {Object} props.localization - Localized strings
 * @param {Object} props.availablePaymentMethods - Available payment method flags
 * @param {Object} props.paymentOptions - Payment options including defaultPayment
 * @param {Object} props.orderDetails - Order details for Apple Pay
 * @param {string} props.installmentValue - Pre-calculated installment value from parent
 * @param {Function} props.onAddNewCard - Handler for opening credit card form
 * @param {Function} props.handleAfterPayExpand - Callback to expand AfterPay view (navigates to L2/L3 state)
 * @param {boolean} props.disableContainerPadding - Disable container padding for reuse in edit view
 * @param {Function} props.handleKlarnaExpand - Callback to expand Klarna view (navigates to L2/L3 state)
 * ([SHP-3366] Implement the new Klarna flow for fric check rel 4)
 */
// eslint-disable-next-line complexity
const NewUserPaymentView = ({
    localization,
    availablePaymentMethods = {},
    _paymentOptions = {},
    orderDetails = {},
    installmentValue,
    onAddNewCard,
    handleAfterPayExpand,
    disableContainerPadding = false,
    withGiftCard = true,
    giftCardPaymentGroups = [],
    showGiftCardAdditionalPaymentRequired = false,
    giftCardAmountEqualsOrderTotal = false,
    onGiftCardSaveAndContinue,
    handleKlarnaExpand,
    handleVenmoExpand,
    handlePazeExpand,
    handlePayWithApplePayClick,
    handlePayPalClick,
    physicalGiftCardShippingGroup,
    onAlternatePaymentDetails,
    isSubscriptionOrder,
    hasNoSavedCreditCards,
    paymentSectionHasError
}) => {
    const [isGiftCardFormOpen, setIsGiftCardFormOpen] = useState(false);

    const {
        isPayPalEnabled,
        isVenmoEnabledForThisOrder,
        isKlarnaEnabledForThisOrder,
        isAfterpayEnabledForThisOrder,
        isAfterpayEnabledForThisProfile,
        isPazeEnabledForThisOrder,
        isPayPalPayLaterEligible
    } = availablePaymentMethods;

    const hasAlternatePayments =
        isPayPalEnabled || isVenmoEnabledForThisOrder || isKlarnaEnabledForThisOrder || isAfterpayEnabledForThisOrder || isPazeEnabledForThisOrder;
    const hasAppliedGiftCards = giftCardPaymentGroups.length > 0;
    const hasReachedMaxGiftCards = giftCardPaymentGroups.length >= 2;
    const shouldShowUseGiftCardControl = !giftCardAmountEqualsOrderTotal && !hasReachedMaxGiftCards;

    // For subscription orders (SDU/AutoReplenish), always show Add New Card button even with full-cover gift card
    // because credit card is required for future charges
    const shouldShowAddNewCardButton = onAddNewCard && (!giftCardAmountEqualsOrderTotal || isSubscriptionOrder);

    // Error message when there is no added credit card for subscription order
    const creditCardRequiredMessage = checkoutUtils.getCreditCardReplenishmentRequiredMessage(orderDetails?.paymentGroups?.paymentMessages);
    const shouldShowCreditCardRequiredMessage = creditCardRequiredMessage && giftCardAmountEqualsOrderTotal && paymentSectionHasError;

    // For subscription orders without saved cards, don't show "Save and Continue" button
    // because there's no payment method to save yet
    const shouldShowGiftCardSaveButton =
        giftCardAmountEqualsOrderTotal && onGiftCardSaveAndContinue && !(isSubscriptionOrder && hasNoSavedCreditCards);

    // Build dynamic list of installment payment methods (only PayPal, Klarna, AfterPay offer installments)
    const installmentMethods = [];

    if (isPayPalPayLaterEligible) {
        installmentMethods.push('PayPal');
    }

    if (isKlarnaEnabledForThisOrder) {
        installmentMethods.push('Klarna');
    }

    if (isAfterpayEnabledForThisOrder && isAfterpayEnabledForThisProfile) {
        installmentMethods.push('AfterPay');
    }

    // Format payment method list: "PayPal, Klarna and AfterPay" or "Klarna and AfterPay"
    const getPaymentMethodsList = () => {
        if (installmentMethods.length === 0) {
            return '';
        }

        if (installmentMethods.length === 1) {
            return installmentMethods[0];
        }

        if (installmentMethods.length === 2) {
            return `${installmentMethods[0]} and ${installmentMethods[1]}`;
        }

        return `${installmentMethods.slice(0, -1).join(', ')} and ${installmentMethods[installmentMethods.length - 1]}`;
    };

    // Show installment message only if we have installment value and at least one installment method
    // Hide Installment message when we have gift cards applied in order
    const showInstallmentMessage = installmentValue && installmentMethods.length > 0 && !hasAppliedGiftCards;

    const isPazeEligible = pazeUtils.isPazeVisible();
    const isApplePayUnavailable = !giftCardAmountEqualsOrderTotal && orderDetails?.header?.isApplePayEnabled === false;
    const unavailableMethods = [];

    // For subscription orders, don't show ApplePay as unavailable
    if (isApplePayUnavailable && !isSubscriptionOrder) {
        unavailableMethods.push('Apple Pay');
    }

    if (!isPayPalEnabled) {
        unavailableMethods.push('PayPal');
    }

    if (!isKlarnaEnabledForThisOrder) {
        unavailableMethods.push('Klarna');
    }

    if (!isAfterpayEnabledForThisOrder || !isAfterpayEnabledForThisProfile) {
        unavailableMethods.push('Afterpay');
    }

    if (!isVenmoEnabledForThisOrder) {
        unavailableMethods.push('Venmo');
    }

    if (isPazeEligible && !isPazeEnabledForThisOrder) {
        unavailableMethods.push('Paze');
    }

    const shouldShowUnavailableMethods = unavailableMethods.length > 0;

    const renderUnavailableMethods = methods =>
        methods.map((method, index) => {
            const isLast = index === methods.length - 1;
            const isSecondLast = index === methods.length - 2;
            const separator = methods.length === 2 || isSecondLast ? localization.and : ', ';

            return (
                <React.Fragment key={method}>
                    <Text fontWeight='bold'>{method}</Text>
                    {!isLast && separator}
                </React.Fragment>
            );
        });

    const handleAlternatePaymentDetails = event => {
        event.preventDefault();
        onAlternatePaymentDetails && onAlternatePaymentDetails();
    };

    // Determine which message to show for unavailable payment methods
    const getUnavailablePaymentMessage = () => {
        if (hasAppliedGiftCards) {
            return localization.cannotBeCombinedWithGiftCards;
        }

        if (physicalGiftCardShippingGroup) {
            return localization.cannotBeUsedWhenPurchasingGiftCards;
        }

        return localization.cantUseForThisOrder;
    };

    return (
        <Box
            paddingX={disableContainerPadding ? 0 : [4, 4, 5]}
            paddingTop={disableContainerPadding ? 0 : [4, 5]}
            paddingBottom={disableContainerPadding ? 0 : [1, 3]}
            data-at={Sephora.debug.dataAt('new_user_payment_view')}
        >
            {/* Add New Card Button */}
            {shouldShowAddNewCardButton && (
                <Button
                    variant='primary'
                    width={['297px', '297px', '50%']}
                    justifyContent='center'
                    onClick={onAddNewCard}
                    aria-label={localization.addNewCard}
                    data-at={Sephora.debug.dataAt('add_new_card_button')}
                    children={localization.addNewCard}
                />
            )}

            {withGiftCard && (
                <>
                    {/* Divider before Gift Card */}
                    <Divider marginY={4} />

                    {hasAppliedGiftCards && (
                        <>
                            <Text
                                is='h3'
                                fontWeight='bold'
                            >
                                {localization.giftCard}
                            </Text>
                            {showGiftCardAdditionalPaymentRequired && (
                                <Text
                                    is='p'
                                    color='red'
                                    marginBottom={[2, 3]}
                                    marginTop={[2, 0]}
                                    lineHeight='tight'
                                >
                                    {localization.giftCardAdditionalPaymentRequired}
                                </Text>
                            )}
                            {shouldShowCreditCardRequiredMessage && (
                                <Text
                                    is='p'
                                    color='red'
                                    marginBottom={[2, 3]}
                                    marginTop={[2, 0]}
                                    lineHeight='tight'
                                >
                                    {creditCardRequiredMessage}
                                </Text>
                            )}
                            <AppliedGiftCards />
                            {shouldShowGiftCardSaveButton && (
                                <Button
                                    variant='primary'
                                    marginTop={4}
                                    width={['100%', '296px']}
                                    onClick={onGiftCardSaveAndContinue}
                                >
                                    {localization.afterpaySaveAndContinue}
                                </Button>
                            )}
                        </>
                    )}

                    {shouldShowUseGiftCardControl && (
                        <UseAGiftCard
                            isNewUserPaymentView={true}
                            withoutLeftMargin={true}
                            hasTitle={!hasAppliedGiftCards}
                            onFormVisibilityChange={setIsGiftCardFormOpen}
                        />
                    )}

                    {hasReachedMaxGiftCards && (
                        <Text
                            is='p'
                            color='gray'
                            fontSize='sm'
                            lineHeight='tight'
                            marginTop={4}
                        >
                            {localization.maxGiftCards}
                        </Text>
                    )}

                    {/* Divider after Gift Card */}
                    {!isGiftCardFormOpen && <Divider marginY={[3, 4]} />}
                </>
            )}

            {/* Other Ways to Pay Section - Pill Button Layout */}
            {hasAlternatePayments && !isGiftCardFormOpen && (
                <Box>
                    <Text
                        is='h3'
                        fontWeight='bold'
                        marginBottom={[1, 2]}
                        aria-level='3'
                    >
                        {localization.otherWaysToPay}
                    </Text>

                    {/* Interest-free payments description - dynamic based on order amount and available methods */}
                    {showInstallmentMessage && (
                        <Text
                            is='p'
                            lineHeight={['18px', 'none']}
                        >
                            {localization.payWith}
                            <b>{` ${StringUtils.format(localization.installmentsMessage, installmentValue)} `}</b>
                            {`${localization.availableWith} `}
                            {getPaymentMethodsList()}
                        </Text>
                    )}

                    {/* Payment Methods - Using Real UFE Components */}
                    <Grid
                        columns={[2, null, 4]}
                        rowGap={4}
                        columnGap={[2, 4]}
                        marginTop={[2, null, 2]}
                        role='group'
                        aria-label='Alternative payment methods'
                    >
                        {!giftCardAmountEqualsOrderTotal && (
                            <Apple
                                isPayWithApplePay={false}
                                handlePayWithApplePayClick={handlePayWithApplePayClick}
                                orderDetails={orderDetails}
                            />
                        )}
                        {!giftCardAmountEqualsOrderTotal && (
                            <Paypal
                                isPayPalEnabled={isPayPalEnabled}
                                handlePaymentClick={handlePayPalClick}
                            />
                        )}
                        <Klarna
                            isKlarnaEnabledForThisOrder={isKlarnaEnabledForThisOrder}
                            handlePaymentClick={handleKlarnaExpand}
                        />
                        <AfterPayPill
                            isAfterpayEnabledForThisOrder={isAfterpayEnabledForThisOrder}
                            isAfterpayEnabledForThisProfile={isAfterpayEnabledForThisProfile}
                            handleAfterPayExpand={handleAfterPayExpand}
                        />
                        <Venmo
                            isVenmoEnabledForThisOrder={isVenmoEnabledForThisOrder}
                            handleVenmoExpand={handleVenmoExpand}
                        />
                        <Paze
                            isPazeEnabledForThisOrder={isPazeEnabledForThisOrder}
                            handlePaymentClick={handlePazeExpand}
                        />
                    </Grid>
                </Box>
            )}
            {shouldShowUnavailableMethods && (
                <Text
                    is='p'
                    fontSize='sm'
                    marginTop={4}
                    lineHeight='tight'
                >
                    {renderUnavailableMethods(unavailableMethods)} {getUnavailablePaymentMessage()}{' '}
                    <Link
                        onClick={handleAlternatePaymentDetails}
                        children={localization.seeDetails}
                        color='blue'
                        css={{ textDecoration: 'underline' }}
                    />
                </Text>
            )}
        </Box>
    );
};

export default wrapFunctionalComponent(NewUserPaymentView, 'NewUserPaymentView');
